import { ChangeEvent, FormEvent, useContext, useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import SocialMediaButtons from './SocialMediaButtons';
import AuthApi from '../../api/authApi';
import UserProfileApi from '../../api/userProfileApi';
import Button from '../../components/atoms/button';
import InputAnimated from '../../components/atoms/input-animated/InputAnimated';
import RoundedImage from '../../components/atoms/rounded-image/RoundedImage';
import TextAreaAnimated from '../../components/atoms/text-area-animated/TextAreaAnimated';
import Card from '../../components/organisms/Card';
import MainArea from '../../components/page-layout/MainArea';
import PageRightSidebar from '../../components/page-layout/PageRightSidebar';
import UserProfileData from '../../types/userProfileData';
import AlertContext from '../../utils/AlertContext';
import { AuthContext } from '../../utils/AuthProvider';
import { getDefaultUserImageUrl } from '../../utils/functions';
import { useFileUpload } from '../../utils/hooks';
import ModalContext from '../../utils/ModalContext';

import './ProfilePage.scss';

function ProfilePage() {
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [imageStoragePath, setImageStoragePath] = useState<string>('');
  const [imageUrl, setImageUrl] = useState<string>('');
  const [instagram, setInstagram] = useState<string>('');
  const [facebook, setFacebook] = useState<string>('');
  const [linkedIn, setLinkedIn] = useState<string>('');
  const [twitter, setTwitter] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [oldPassword, setOldPassword] = useState<string>('');
  const [researchConsent, setResearchConsent] = useState<boolean>(false);

  const { showAlertModal } = useContext(ModalContext);
  const showAlert = useContext(AlertContext);
  const { user, userProfile, setUserProfile } = useContext(AuthContext)!;

  const [imageToUpload, setImageToUpload] = useState<File | 'DELETE_ONLY'>();
  const { previewUrl, updateStorage } = useFileUpload(
    imageToUpload,
    userProfile?.imageStoragePath,
    [imageStoragePath, setImageStoragePath],
    'images/user-profiles'
  );

  const navigate = useNavigate();
  const { showAgreementModal } = useContext(ModalContext);

  useEffect(() => {
    if (user) {
      setEmail(user.email!);
      if (userProfile) {
        setFirstName(userProfile.firstName || '');
        setLastName(userProfile.lastName || '');
        setTitle(userProfile.title || '');
        setDescription(userProfile.description || '');
        setImageUrl(userProfile.imageUrl);
        setImageStoragePath(userProfile.imageStoragePath || '');
        setInstagram(userProfile.instagram || '');
        setFacebook(userProfile.facebook || '');
        setLinkedIn(userProfile.linkedIn || '');
        setTwitter(userProfile.twitter || '');
        setResearchConsent(userProfile.researchConsent || false);
      }
    }
  }, [user, userProfile]);

  useEffect(() => {
    if (previewUrl) {
      setImageUrl(previewUrl);
    } else if (userProfile?.imageUrl) {
      setImageUrl(userProfile.imageUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want changes to previewUrl to trigger the effect
  }, [previewUrl, userProfile?.imageUrl]);

  const socialLinkValues = {
    instagram,
    facebook,
    linkedIn,
    twitter,
    setInstagram,
    setFacebook,
    setLinkedIn,
    setTwitter,
  };

  async function updatePassword() {
    if (!password) {
      return true;
    }
    if (confirmPassword !== password) {
      showAlertModal('New and confirm passwords do not match');
      return false;
    }
    // Double-check the old password
    const reAuthenticated = await AuthApi.reauthenticateUser(oldPassword);
    if (!reAuthenticated) {
      showAlertModal('The old password is incorrect');
      return false;
    }

    const passwordUpdateSuccessful = await AuthApi.updateUserPassword(confirmPassword);
    if (!passwordUpdateSuccessful) {
      showAlertModal('Could not update password');
      return false;
    }
    return true;
  }

  function handleImageChange(e: ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setImageToUpload(file);
    }
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!(await updatePassword())) {
      return;
    }
    await updateStorage();
    const newUserProfile = {
      uuid: user!.uid,
      firstName,
      lastName,
      title,
      imageStoragePath,
      imageUrl,
      description,
      instagram,
      facebook,
      linkedIn,
      twitter,
      role: 'MEMBER' as const,
      researchConsent,
    };
    let id: string;
    if (!userProfile) {
      id = await createUserProfile(newUserProfile);
    } else {
      await updateUserProfile({ ...newUserProfile, id: userProfile.id, researchConsent: userProfile.researchConsent });
      id = userProfile.id;
    }
    setUserProfile({ ...newUserProfile, id });
    showAlert('Changes saved successfully!');
  }

  async function createUserProfile(newUserProfile: Omit<UserProfileData, 'id'>) {
    return await UserProfileApi.addUserProfile(newUserProfile);
  }

  async function updateUserProfile(newUserProfile: UserProfileData) {
    await UserProfileApi.updateUserProfile(newUserProfile.id, newUserProfile);
  }

  function handleDelete() {
    setImageToUpload('DELETE_ONLY');
  }

  return (
    <PageRightSidebar className="profile-page">
      <MainArea>
        <Card className="content-container p-16">
          {previewUrl !== undefined && (
            <RoundedImage
              className="profile-image-position"
              src={previewUrl ? previewUrl : getDefaultUserImageUrl()}
              alt="User uploaded profile"
              size="lg"
            />
          )}
          <div className="p-12">
            <form className="form" onSubmit={handleSubmit}>
              <h1 className="strong-heading mb-8">
                {userProfile ? 'Hello ' + userProfile.firstName + '!' : 'Welcome! Create your new profile below'}
              </h1>
              <div className="social-media-container">
                <SocialMediaButtons {...socialLinkValues} />
              </div>
              <div>
                <InputAnimated className="w-full mb-6" placeholder={'Email'} value={email} setState={setEmail} />
                <div className="name">
                  <InputAnimated
                    className="w-full mb-6"
                    placeholder={'First name'}
                    value={firstName}
                    setState={setFirstName}
                    required
                  />
                  <InputAnimated
                    className="w-full mb-6"
                    placeholder={'Last name'}
                    value={lastName}
                    setState={setLastName}
                    required
                  />
                </div>
                <InputAnimated className="w-full mb-8" placeholder={'Title'} value={title} setState={setTitle} />
              </div>
              <div className="image-input-container mb-8">
                <p className="font-medium heading-area whitespace-nowrap">Profile image</p>
                {previewUrl !== undefined && (
                  <RoundedImage
                    className="image-area"
                    src={previewUrl || getDefaultUserImageUrl()}
                    alt="User uploaded profile"
                  />
                )}
                <input id="image-file" type="file" hidden onChange={handleImageChange} />
                <div className="buttons-area flex items-center gap-12">
                  {previewUrl !== undefined && (
                    <label className="color-blue font-medium update-edit-button" htmlFor="image-file">
                      {previewUrl === null ? 'Upload' : 'Edit'}
                    </label>
                  )}
                  {previewUrl && (
                    <button className="font-medium" type="button" onClick={handleDelete}>
                      Remove
                    </button>
                  )}
                </div>
              </div>
              <fieldset className="separator-line">
                <legend className="font-medium mb-6">Change password</legend>
                <InputAnimated
                  className="w-full mb-6"
                  placeholder={'New password'}
                  value={password}
                  type="password"
                  setState={setPassword}
                  autoComplete={'on'}
                />
                <InputAnimated
                  className="w-full mb-6"
                  placeholder={'Confirm password'}
                  value={confirmPassword}
                  type="password"
                  setState={setConfirmPassword}
                  autoComplete={'on'}
                />
                <InputAnimated
                  className="w-full"
                  placeholder={'Old password'}
                  value={oldPassword}
                  type="password"
                  setState={setOldPassword}
                  autoComplete={'off'}
                />
              </fieldset>
              <TextAreaAnimated className="mb-8" placeholder={'Bio'} value={description} setState={setDescription} />
              {userProfile === null && (
                <div className="flex flex-wrap items-center mb-4">
                  <input
                    className="mr-2"
                    id="researchConsent"
                    type="checkbox"
                    checked={researchConsent}
                    onChange={e => setResearchConsent(e.target.checked)}
                  />
                  <label className="!font-normal" htmlFor="consent">
                    I hereby consent to sharing my data in the
                  </label>
                  &nbsp;
                  <button onClick={() => showAgreementModal(1)} type="button" className="link-button">
                    research on lifelong learning
                  </button>
                </div>
              )}
              <div className="buttons">
                <Button type="submit">{userProfile !== undefined ? 'Update' : 'Save'}</Button>
                <Button visualType="secondary" onClick={() => navigate(-1)}>
                  Discard
                </Button>
              </div>
            </form>
          </div>
        </Card>
      </MainArea>
    </PageRightSidebar>
  );
}

export default ProfilePage;
